// Single source of truth for app-download links.
//
// The QR code on the site (public/qr-download.svg) encodes DOWNLOAD_URL.
// The store badges link to APP_STORE_URL / PLAY_STORE_URL.
//
// LIVE since 2026-08-30: both stores are published and these point at the real
// listings. The QR still encodes DOWNLOAD_URL on purpose, /download is the
// device-agnostic landing page and works from any camera.

export const DOWNLOAD_PATH = "/download";
export const DOWNLOAD_URL = "https://www.letsloop.app/download";

// Canonical WhatsApp community invite. The primary "Join the community" CTA
// across the site (navbar, footer, newsletter, events pages) resolves here so
// the invite link lives in exactly one place.
export const WHATSAPP_COMMUNITY_URL =
  "https://chat.whatsapp.com/LoYOzet2vNjH4brLAyPHb6?mode=gi_t";

// Real store listings. Region-neutral Apple URL so a non-UK visitor lands in
// their own storefront rather than being bounced through /gb/.
export const APP_STORE_URL = "https://apps.apple.com/app/id6765853884";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.letsloop.app";

// True today, no fabricated numbers. Used as the hero/download trust trio
// (the honest stand-in for Peanut's "Rated 4.4 / 5M+"). Swap to real
// rating + member/loop counts once they exist and are ASA-safe.
export const TRUST_TRIO: readonly string[] = [
  "Free to join",
  "London-wide",
  "iOS & Android",
];

// --- Beta / soft-launch access (thread LL-0141) ---
// The beta QR (public/beta-qr.png) encodes BETA_URL, the /beta detect-and-redirect page.
// Minimum-friction soft launch: get people into the beta in as few taps as possible,
// never a waitlist (founder brief, Adrien 2026-06-23).
//
// TODO(founder): once the app is on TestFlight (PUBLIC link) and Google Play OPEN testing,
// set TESTFLIGHT_URL + PLAY_BETA_URL to the real opt-in links and flip BETA_LIVE to true.
// While BETA_LIVE is false, /beta shows the instant-access email capture (no dead links).
export const BETA_PATH = "/beta";
export const BETA_URL = "https://www.letsloop.app/beta";
export const BETA_LIVE = false;
export const TESTFLIGHT_URL = BETA_URL; // -> "https://testflight.apple.com/join/XXXXXXXX"
export const PLAY_BETA_URL = BETA_URL; //  -> "https://play.google.com/apps/testing/app.letsloop"

// --- Web account / desktop PWA (footer "Your account" column) ---
// LetsLoop's account lives in the app today. The desktop browser PWA
// (Phase 1: Expo Web at app.letsloop.app) is the planned home for
// log-in / sign-up / settings from a laptop.
//
// While WEB_APP_LIVE is false, the account links point at /download (the
// honest "create your account in the app" path) so the footer never ships a
// dead auth link. Flip WEB_APP_LIVE to true and the log-in / sign-up links
// switch to the PWA automatically. Keep this the single source of truth.
export const WEB_APP_URL = "https://app.letsloop.app";
export const WEB_APP_LIVE = false;

// Resolved account-link targets for the footer. A target is either an internal
// route ({ to }, rendered with react-router) or an external URL ({ href },
// rendered as a new-tab anchor). While WEB_APP_LIVE is false they route to the
// internal /download page (create your account in the app); flip the flag and
// they become external links into the desktop PWA. The footer renders on this
// shape and never re-reads WEB_APP_LIVE, so this module stays the single source
// of truth for the flip.
export type AccountLinkTarget = { to: string } | { href: string };
export const LOGIN_LINK: AccountLinkTarget = WEB_APP_LIVE
  ? { href: `${WEB_APP_URL}/login` }
  : { to: DOWNLOAD_PATH };
export const SIGNUP_LINK: AccountLinkTarget = WEB_APP_LIVE
  ? { href: `${WEB_APP_URL}/signup` }
  : { to: DOWNLOAD_PATH };
